import { FormEvent, useCallback, useState } from 'react';
import MessageEditor from './MessageEditor';
import './PlaceholderPage.css';

function PlaceholderPage() {
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error' | 'invalid'>('idle');
  const [message, setMessage] = useState('');
  const [html, setHtml] = useState('');
  const [resetVersion, setResetVersion] = useState(0);
  const updateMessage = useCallback((text: string, markup: string) => {
    setMessage(text);
    setHtml(markup);
    setStatus(current => current === 'sending' ? current : 'idle');
  }, []);

  async function sendMessage(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (status === 'sending') return;
    const form = event.currentTarget;
    if (!form.reportValidity()) return;
    if (!message.trim() || message.trim().length > 5000 || html.length > 50000) {
      setStatus('invalid');
      return;
    }

    const fields = new FormData(form);
    setStatus('sending');

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: fields.get('email'),
          message,
          html,
          website: fields.get('website'),
        }),
        signal: AbortSignal.timeout(15000),
      });
      const result = await response.json();
      if (!response.ok || result.ok !== true) throw new Error('Send failed');
      form.reset();
      setResetVersion(version => version + 1);
      setMessage('');
      setHtml('');
      setStatus('sent');
    } catch {
      setStatus('error');
    }
  }

  return (
    <main className="placeholder-page">
      <section className="contact-card" aria-labelledby="compose-title">
        <header className="compose-header">
          <h1 id="compose-title">New Message</h1>
        </header>
        <form onSubmit={sendMessage} onChange={() => {
          if (status !== 'sending') setStatus('idle');
        }}>
          <fieldset disabled={status === 'sending'}>
            <div className="compose-row">
              <span className="compose-label">To:</span>
              <span className="recipient-chip">
                <img className="recipient-avatar" src="/ddl.jpg" alt="" width={24} height={24} />
                Daniel Longo
              </span>
            </div>

            <div className="compose-row">
              <label className="compose-label" htmlFor="email">Reply to:</label>
              <input id="email" name="email" type="email" aria-label="your email" autoComplete="email" maxLength={254} required />
            </div>

            <MessageEditor disabled={status === 'sending'} resetVersion={resetVersion} onChange={updateMessage} />

            <div className="contact-trap" aria-hidden="true">
              <label htmlFor="website">leave this blank</label>
              <input id="website" name="website" type="text" tabIndex={-1} autoComplete="off" />
            </div>

            <footer className="compose-footer">
              <button className="send-button" type="submit">{status === 'sending' ? 'Sending…' : 'Send'}</button>
              <p className="contact-status" role="status" aria-live="polite">
                {status === 'sent' && 'thanks — your message was sent.'}
                {status === 'error' && 'couldn’t send. your message is still here; please try again.'}
                {status === 'invalid' && 'Please write a message of up to 5,000 characters with simple formatting.'}
              </p>
            </footer>
          </fieldset>
        </form>
      </section>
    </main>
  );
}

export default PlaceholderPage;
